import { filterSpecificPlaces } from "./place-term-classification";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import {
  detectFlexiblePlaceRequest,
  detectRequestedStopCategory,
  extractBasicTripRequest,
  extractDeterministicTripIntent,
  hasExplicitStartTime,
  isArrivalTransportOnly,
  type GeographicScope,
  type RequestedStopCategory,
} from "./trip-intent";
import { detectExclusiveCategory, detectExplicitTourismFamilies, EXCLUSIVE_TO_FOCUS } from "./exclusive-category";
import { detectLocalSpecialtyIntent } from "./local-specialty-intent";
import { classifyTripComplexity, type TripComplexity } from "./trip-complexity";
import { isChargeablePromptResult } from "./quota-reservation-policy";
import { parseTransportPlan } from "./request-contract";
import { resolveTransportExecution, type TransportMode } from "./transport-modes";
import { extractRequiredStopConstraints } from "./required-constraints";
import { parseRelativeDayWindow } from "./relative-day-window";

const Input = z.object({
  prompt: z.string().min(2).max(800),
  language: z.string().min(2).max(8),
  anonId: z.string().max(80).default(""),
});

const INTEREST_IDS = ["cultural", "food", "viewpoint", "nature", "shopping", "nightlife", "family", "hidden"] as const;
const MODE_IDS = ["walk", "scooter", "bike", "car", "transit"] as const;
export const CATEGORY_FOCUS_IDS = [
  "cafe", "restaurant", "hotel", "viewpoint", "museum", "monument", "church",
  "park", "nature", "water", "shopping", "nightlife", "market", "sweets", "breakfast",
] as const;
export type CategoryFocus = (typeof CATEGORY_FOCUS_IDS)[number];

import type { RequiredStopConstraint } from "./required-constraints";

export type ParsedTripPrompt = {
  city?: string;
  country?: string;
  countryCode?: string;
  lat?: number;
  lng?: number;
  durationHours?: number;
  startTime?: string;
  interests?: string[];
  mode?: string;
  /** Modo usado dentro de cada zona num pedido híbrido. */
  localMode?: string;
  includeRestaurant?: boolean;
  startFromUserLocation?: boolean;
  startPlace?: string;
  notes?: string;
  exhaustive?: boolean;
  categoryFocus?: CategoryFocus;
  requestedStopCategory?: RequestedStopCategory;
  requestedStopQuery?: string;
  categoryFocusQuery?: string;
  namedRoute?: string;
  requiredPlaces?: string[];
  /** Restrições de paragens extraídas localmente (categorias/tipos/cozinhas). */
  requiredStopConstraints?: RequiredStopConstraint[];
  geographicScope?: GeographicScope;
  complexity?: TripComplexity;
};

function premiumMode(mode: TransportMode): (typeof MODE_IDS)[number] {
  return mode === "motorcycle" ? "car" : mode;
}

function applyDeterministicTransport(parsed: ParsedTripPrompt, prompt: string) {
  const plan = parseTransportPlan(prompt, (parsed.mode as TransportMode | undefined) ?? "walk");
  if (plan.source !== "text") return;
  const execution = resolveTransportExecution(plan);
  parsed.mode = premiumMode(execution.betweenZones);
  parsed.localMode =
    execution.withinZone !== execution.betweenZones
      ? premiumMode(execution.withinZone)
      : undefined;
}

export type ParseTripResult = {
  parsed: ParsedTripPrompt | null;
  error: string | null;
  rateLimited?: boolean;
  tier?: "anon" | "free" | "premium" | "admin";
  remaining?: number;
};

const normalizeBrief = (value: string) => value.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

function extractCriticalRouteConstraint(prompt: string): string {
  const fragments = prompt.split(/[.!?\n]+/).map((part) => part.trim()).filter(Boolean);
  const patterns = [
    /(?:acaba(?:r)?|acabe|termina(?:r)?|termine|finaliza(?:r)?|chega(?:r)?|chegue|finish|end|arrive)\b.*?(?:perto|pr[oó]ximo|junto|near|close\s+to|at|em|no|na|ao|à)\b/i,
    /(?:começa(?:r)?|comece|inicia(?:r)?|part(?:e|ir)|start|begin)\b.*?(?:perto|pr[oó]ximo|junto|near|close\s+to|at|em|no|na|ao|à)\b/i,
  ];
  return fragments.find((fragment) => patterns.some((pattern) => pattern.test(fragment)))?.slice(0, 180) ?? "";
}

function detectExhaustiveIntent(prompt: string): boolean {
  return /(todos\s+os|todas\s+as|tudo\s+o\s+que|lista\s+completa|o\s+m[aá]ximo\s+de|quantos?\s+houver|all\s+the\s+\w+|every\s+\w+|complete\s+list|as\s+many\s+as|comprehensive|todo\s+lo|toutes\s+les|tous\s+les)/i.test(prompt.toLowerCase());
}

function detectCategoryFocus(prompt: string): CategoryFocus | undefined {
  const p = prompt.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const patterns: Array<{ id: CategoryFocus; re: RegExp }> = [
    { id: "cafe", re: /\b(cafes?|coffee\s?shops?|cafeter[ií]as?|pastelarias?|padarias?)\b/ },
    { id: "restaurant", re: /\b(restaurantes?|restaurants?|tascas?|bistr[oô]s?|places?\s+to\s+eat|onde\s+comer)\b/ },
    { id: "hotel", re: /\b(hot[eé]is|hotels?|hostels?|pousadas?|alojament\w*|accommodation|where\s+to\s+stay|onde\s+dormir|onde\s+ficar)\b/ },
    { id: "viewpoint", re: /\b(miradour\w+|viewpoints?|vistas?\s+panor\w+|panor[aâ]micas?|overlooks?)\b/ },
    { id: "museum", re: /\b(museus?|museums?|mus[eé]es?|museos?)\b/ },
    { id: "monument", re: /\b(monumentos?|monuments?)\b/ },
    { id: "church", re: /\b(igrejas?|catedrais?|cathedrals?|s[eé]s|churches?|eglises?)\b/ },
    { id: "park", re: /\b(parques?\s+urbanos?|jardins?\s+p[uú]blicos?|jardins?\s+bot[aâ]nicos?|parks?|gardens?)\b/ },
    { id: "water", re: /\b(rios?|lagos?|lagoas?|ribeiros?|praias?|mar|costa|rivers?|lakes?|beaches?|seaside|waterfront)\b/ },
    { id: "nature", re: /\b(natureza|trilhos?|hikes?|hiking|serras?|forests?|florestas?)\b/ },
    { id: "shopping", re: /\b(shoppings?|lojas?|shops?|compras|shopping\s+malls?)\b/ },
    { id: "nightlife", re: /\b(bares?|bars?|discotecas?|clubs?|nightlife|vida\s+noturna)\b/ },
    { id: "market", re: /\b(mercados?|markets?|feiras?|farmers'?\s+markets?)\b/ },
    { id: "breakfast", re: /\b(pequenos?[\s-]*almo[cç]os?|brunch(?:es)?|breakfasts?|desayunos?|colazione|fruhstuck)\b/ },
    { id: "sweets", re: /\b(doces?\s+(?:regionais?|t[ií]picos?|tradicionais?|conventuais?)|do[cç]aria|pastelarias?|padarias?\s+(?:t[ií]picas?|tradicionais?)|confeitarias?|sweets?\s+shop|pastry\s+shop|patisserie|dolci\s+t[ií]pici|pasteles?\s+t[ií]picos)\b/ },
  ];
  const narrowing = /\b(so|apenas|somente|unicamente|only|just|nada\s+mais|except|except\s+for|nothing\s+but)\b|(todos\s+os|todas\s+as|all\s+the|every)\b/;
  if (!narrowing.test(p)) return undefined;
  return patterns.find(({ re }) => re.test(p))?.id;
}

/**
 * "só restaurantes", "apenas miradouros", "only museums" — contrato rígido.
 * O foco exclusivo manda sobre um pedido de paragem individual: pedir "só
 * restaurantes" nunca pode virar "uma paragem de restaurante num roteiro".
 */
function applyExclusiveCategoryFocus(parsed: ParsedTripPrompt, prompt: string) {
  const exclusive = detectExclusiveCategory(prompt);
  // Especialidade gastronómica local: quando o único objetivo é comer/provar/
  // comprar doçaria, o foco é vinculativo mesmo sem "só".
  const specialty = detectLocalSpecialtyIntent(prompt);
  // Autoridade multi-família (taxonomia global): quando o pedido menciona
  // famílias turísticas explícitas (ex.: "só museus e miradouros"), o foco
  // único legado NUNCA é aplicado — só os fluxos de comida (exclusive/
  // specialty) mantêm o comportamento existente.
  const tourismFamilies = detectExplicitTourismFamilies(prompt);
  const focus = exclusive
    ? (EXCLUSIVE_TO_FOCUS[exclusive.category] as CategoryFocus)
    : specialty.binding && specialty.focus
      ? (specialty.focus as CategoryFocus)
      : tourismFamilies.length > 0
        ? undefined
        : detectCategoryFocus(prompt);
  if (!focus) return;
  parsed.categoryFocus = focus;
  parsed.requestedStopCategory = undefined;
  parsed.requestedStopQuery = undefined;
  if (exclusive || specialty.binding) parsed.categoryFocusQuery = undefined;
}


function enrichNotesWithHardConstraints(parsed: ParsedTripPrompt, language: string) {

  const parts = [parsed.notes?.trim() ?? ""];
  const pt = language.toLowerCase().startsWith("pt");
  if (parsed.startPlace) {
    parts.push(pt
      ? `PONTO DE PARTIDA OBRIGATÓRIO: ${parsed.startPlace}. Começa o roteiro neste local; não peças GPS nem uses o centro da cidade como origem.`
      : `MANDATORY STARTING POINT: ${parsed.startPlace}. Start the route from this place; do not request GPS or use the city centre as the origin.`);
  }
  if (parsed.namedRoute) {
    parts.push(pt
      ? `ROTA NOMEADA OBRIGATÓRIA: ${parsed.namedRoute}. Identifica e respeita os pontos reais desta rota; não a substituas por atrações genéricas.`
      : `MANDATORY NAMED ROUTE: ${parsed.namedRoute}. Identify and respect its real stops; do not replace it with generic attractions.`);
  }
  if (parsed.requiredPlaces?.length) {
    parts.push(pt
      ? `LOCAIS OBRIGATÓRIOS: ${parsed.requiredPlaces.join("; ")}. Inclui todos os que forem encontrados e validados; não os substituas por sugestões.`
      : `MANDATORY PLACES: ${parsed.requiredPlaces.join("; ")}. Include every place that can be found and validated; do not replace them with suggestions.`);
  }
  if (parsed.requiredStopConstraints?.length) {
    const labels = parsed.requiredStopConstraints.map((c) => {
      if (c.kind === "restaurant" && c.cuisine) return `${c.cuisine} restaurant`;
      if (c.activityType) return c.activityType.replace(/_/g, " ");
      return c.query;
    });
    parts.push(pt
      ? `PARAGENS OBRIGATÓRIAS POR CATEGORIA: ${labels.join("; ")}. Inclui pelo menos uma paragem real de cada tipo; não substituas por atrações genéricas.`
      : `MANDATORY STOP CATEGORIES: ${labels.join("; ")}. Include at least one real stop of each type; do not replace them with generic attractions.`);
  }
  if (parsed.geographicScope && parsed.geographicScope !== "city") {
    parts.push(pt
      ? `ÂMBITO GEOGRÁFICO: ${parsed.geographicScope}. Não limites artificialmente o pedido ao centro administrativo de uma única cidade.`
      : `GEOGRAPHIC SCOPE: ${parsed.geographicScope}. Do not artificially restrict the request to one city's administrative centre.`);
  }
  parsed.notes = parts.filter(Boolean).join(" ").slice(0, 700) || undefined;
}

export function buildDeterministicPromptResult(
  prompt: string,
  language: string,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  now = new Date(),
): ParsedTripPrompt | null {
  const basic = extractBasicTripRequest(prompt);
  if (!basic.city) return null;

  // Janela temporal relativa ("amanhã das 13h às 16h") fornece duração e
  // hora de início quando o texto não as declara explicitamente.
  const window = parseRelativeDayWindow(prompt, timeZone, now);
  const durationHours = basic.durationHours ?? window?.durationHours;
  const startTime = basic.startTime ?? (window?.startMinutes != null
    ? `${String(Math.floor(window.startMinutes / 60)).padStart(2, "0")}:${String(window.startMinutes % 60).padStart(2, "0")}`
    : undefined);

  const deterministic = extractDeterministicTripIntent(prompt);
  const flexiblePlaceRequest = detectFlexiblePlaceRequest(prompt, deterministic.requiredPlaces);
  const parsed: ParsedTripPrompt = {
    city: basic.city,
    durationHours,
    startTime,
    mode: basic.mode,
    startPlace: deterministic.startPlace,
    startFromUserLocation: deterministic.startFromUserLocation,
    namedRoute: deterministic.namedRoute,
    requiredPlaces: deterministic.requiredPlaces,
    requiredStopConstraints: extractRequiredStopConstraints(prompt),
    geographicScope: deterministic.geographicScope,
    requestedStopCategory: detectRequestedStopCategory(prompt, deterministic.requiredPlaces),
    requestedStopQuery: flexiblePlaceRequest.requestedStopQuery,
    categoryFocusQuery: flexiblePlaceRequest.categoryFocusQuery,
    notes: prompt.trim().slice(0, 300),
  };
  applyExclusiveCategoryFocus(parsed, prompt);
  applyDeterministicTransport(parsed, prompt);
  parsed.complexity = classifyTripComplexity({
    durationHours: parsed.durationHours,
    geographicScope: parsed.geographicScope,
    namedRoute: parsed.namedRoute,
    requiredPlaces: parsed.requiredPlaces,
    mode: parsed.mode,
    notes: prompt,
  });
  enrichNotesWithHardConstraints(parsed, language);
  return parsed;
}

/**
 * Pedidos urbanos standard já totalmente interpretados por regras locais não
 * devem reservar quota nem chamar o parser de IA. O texto integral segue nas
 * notas para o gerador de paragens, por isso não se perde a intenção.
 */
export function canUseDeterministicPromptResult(
  parsed: ParsedTripPrompt | null,
): parsed is ParsedTripPrompt {
  if (!parsed?.city?.trim()) return false;
  if (!parsed.durationHours || parsed.durationHours <= 0) return false;
  // Pedidos com restrições de paragens locais podem usar o caminho
  // determinístico mesmo quando o contrato é ligeiramente mais complexo,
  // porque a validação de disponibilidade acontece antes da geração.
  if (parsed.requiredStopConstraints?.length || parsed.requiredPlaces?.length) {
    return parsed.complexity?.level !== "advanced" && (!parsed.geographicScope || parsed.geographicScope === "city");
  }
  if (parsed.complexity?.level !== "standard") return false;
  return !parsed.geographicScope || parsed.geographicScope === "city";
}

export const parseTripPrompt = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => Input.parse(d))
  .handler(async ({ data }): Promise<ParseTripResult> => {
    const fallback = buildDeterministicPromptResult(data.prompt, data.language);
    // Caminho sem custo: não consulta o fornecedor de IA e não toca na quota.
    if (canUseDeterministicPromptResult(fallback)) {
      return { parsed: fallback, error: null };
    }
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) return { parsed: fallback, error: fallback ? null : "AI not configured" };

    const { checkAiPromptRateLimit, releaseQuotaInternal } = await import("./quota.server");
    const quota = await checkAiPromptRateLimit(data.anonId);
    if (!quota.ok) return { parsed: null, error: "rate_limited", rateLimited: true, tier: quota.tier };

    const releaseFailedParse = async () => {
      const quotaReleased = await releaseQuotaInternal(quota);
      if (!quotaReleased) {
        console.error("CRITICAL: failed to release AI prompt quota", {
          reservationId: quota.reservation.usageCounterId,
        });
      }
      return quotaReleased;
    };

    const sys = `You are a tourism request parser. Read a free-form trip request (in any language) and extract structured fields. Reply ONLY through the tool call.

Rules:
- "city" / "country" / "countryCode" (ISO-2): only if the user clearly names a place. Provide approximate "lat" and "lng" for that place's centre when known.
- "durationHours": convert trip duration expressions to hours. Omit if not stated.
- "startTime": planned arrival/route-start clock time in HH:MM when stated (for example "perto das 11 horas" → "11:00"). Never confuse this with durationHours.
- "interests": pick from: ${INTEREST_IDS.join(", ")}.
- "mode": ${MODE_IDS.join(", ")}. Default omit.
- "includeRestaurant": true if the user wants a restaurant or meal recommendation.
- "startFromUserLocation": true ONLY when the user says they are physically at/near the destination NOW or explicitly asks to use their current GPS location. Set false for future planning, future arrival, or a named starting point.
- "startPlace": exact named place where a future route must begin (station, airport, hotel, landmark, address). Example: "vou chegar a São Bento" means startPlace="São Bento" and startFromUserLocation=false.
- "notes": short distilled brief in the user's language, preserving exact start/end constraints.
- "exhaustive": true if the user asks for every/all notable place.
- "categoryFocus": ONLY when the whole itinerary is restricted to one category, use one of ${CATEGORY_FOCUS_IDS.join(", ")}.
- "requestedStopCategory": when the user asks to recommend/include ONE unspecified place of a known category inside an otherwise normal itinerary, use one of ${CATEGORY_FOCUS_IDS.join(", ")}.
- "requestedStopQuery": concise Google Places search query in English when the user asks for ONE unspecified venue type not fully covered by requestedStopCategory (for example "ice cream shop", "library", "bookstore"). Never put a specific proper name here.
- "categoryFocusQuery": concise Google Places search query in English when the WHOLE itinerary is restricted to any venue type or theme not fully covered by categoryFocus.
- "requiredPlaces": exact named venues the user explicitly wants included. Preserve shops, pastry shops, cafés, restaurants, shopping centres and landmarks; never turn a named place into a generic category.
- "namedRoute": exact official/known route name when explicitly requested.
- "geographicScope": one of city, regional, named_route, multi_city when clear.
Omit unknown fields rather than guessing.`;

    try {
      const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
        method: "POST",
        signal: AbortSignal.timeout(15000),
        headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "google/gemini-2.5-flash-lite",
          messages: [
            { role: "system", content: sys },
            { role: "user", content: `User prompt (language code "${data.language}"): """${data.prompt}"""` },
          ],
          tools: [{
            type: "function",
            function: {
              name: "return_trip",
              parameters: {
                type: "object",
                properties: {
                  city: { type: "string" }, country: { type: "string" },
                  countryCode: { type: "string", description: "ISO 3166-1 alpha-2" },
                  lat: { type: "number" }, lng: { type: "number" },
                  durationHours: { type: "number", minimum: 0.25, maximum: 336 },
                  startTime: { type: "string", pattern: "^[0-2]?[0-9]:[0-5][0-9]$" },
                  interests: { type: "array", items: { type: "string", enum: [...INTEREST_IDS] } },
                  mode: { type: "string", enum: [...MODE_IDS] },
                  includeRestaurant: { type: "boolean" }, startFromUserLocation: { type: "boolean" },
                  startPlace: { type: "string" },
                  notes: { type: "string" }, exhaustive: { type: "boolean" },
                  categoryFocus: { type: "string", enum: [...CATEGORY_FOCUS_IDS] },
                  requestedStopCategory: { type: "string", enum: [...CATEGORY_FOCUS_IDS] },
                  requestedStopQuery: { type: "string", maxLength: 100 },
                  categoryFocusQuery: { type: "string", maxLength: 100 },
                  namedRoute: { type: "string" },
                  requiredPlaces: { type: "array", items: { type: "string" }, maxItems: 12 },
                  geographicScope: { type: "string", enum: ["city", "regional", "named_route", "multi_city"] },
                },
                additionalProperties: false,
              },
            },
          }],
          tool_choice: { type: "function", function: { name: "return_trip" } },
        }),
      });

      if (!res.ok) {
        const body = await res.text().catch(() => "");
        console.error("Lovable AI prompt parse error", res.status, body);
        await releaseFailedParse();
        if (fallback) return { parsed: fallback, error: null, tier: quota.tier, remaining: quota.remaining };
        if (res.status === 429) return { parsed: null, error: "ai_rate_limited" };
        if (res.status === 402) return { parsed: null, error: "ai_credits" };
        return { parsed: null, error: "ai_error" };
      }

      const json = await res.json();
      const args = json.choices?.[0]?.message?.tool_calls?.[0]?.function?.arguments;
      const modelParsed = args ? (JSON.parse(args) as ParsedTripPrompt) : null;
      const chargeable = isChargeablePromptResult(modelParsed);
      if (!chargeable) await releaseFailedParse();
      const parsed = chargeable ? modelParsed : fallback;
      const deterministic = extractDeterministicTripIntent(data.prompt);
      const flexiblePlaceRequest = detectFlexiblePlaceRequest(data.prompt, deterministic.requiredPlaces);
      const basic = extractBasicTripRequest(data.prompt);
      const criticalConstraint = extractCriticalRouteConstraint(data.prompt);

      if (parsed && criticalConstraint) {
        const currentNotes = parsed.notes?.trim() ?? "";
        if (!normalizeBrief(currentNotes).includes(normalizeBrief(criticalConstraint))) {
          parsed.notes = [currentNotes, criticalConstraint].filter(Boolean).join(" ").slice(0, 300);
        }
      }
      if (parsed) {
        // Prefer the deterministic city when the wording is explicit. This prevents\n        // AI values such as "Porto perto das 11 horas" from reaching geocoding.\n        if (basic.city) parsed.city = basic.city;
        if (!parsed.durationHours && basic.durationHours) parsed.durationHours = basic.durationHours;
        if (basic.startTime) {
          parsed.startTime = basic.startTime;
        } else if (!hasExplicitStartTime(data.prompt)) {
          // The model must never turn "1 day" or another duration into 00:00.
          // No clock in the user's words means no parsed clock value.
          parsed.startTime = undefined;
        }
        if (isArrivalTransportOnly(data.prompt) && parsed.mode === "transit" && !basic.mode) {
          parsed.mode = undefined;
        } else if (!parsed.mode && basic.mode) {
          parsed.mode = basic.mode;
        }
        applyDeterministicTransport(parsed, data.prompt);
        if (!parsed.exhaustive && detectExhaustiveIntent(data.prompt)) parsed.exhaustive = true;
        const requestedStopCategory = detectRequestedStopCategory(data.prompt, deterministic.requiredPlaces);
        if (!parsed.requestedStopCategory && requestedStopCategory) {
          parsed.requestedStopCategory = requestedStopCategory;
        }
        if (!parsed.requestedStopQuery && flexiblePlaceRequest.requestedStopQuery) {
          parsed.requestedStopQuery = flexiblePlaceRequest.requestedStopQuery;
        }
        if (!parsed.categoryFocusQuery && flexiblePlaceRequest.categoryFocusQuery) {
          parsed.categoryFocusQuery = flexiblePlaceRequest.categoryFocusQuery;
        }
        if (parsed.requestedStopCategory || parsed.requestedStopQuery) {
          // Asking for one venue inside a normal route is not the same as
          // asking for an itinerary made only of that category.
          parsed.categoryFocus = undefined;
          parsed.categoryFocusQuery = undefined;
        } else if (!parsed.categoryFocus) {
          parsed.categoryFocus = detectCategoryFocus(data.prompt);
        }
        // O contrato exclusivo é o último a falar: nem o modelo nem o pedido
        // de paragem individual podem apagar um "só restaurantes".
        applyExclusiveCategoryFocus(parsed, data.prompt);
        if (deterministic.namedRoute) parsed.namedRoute = deterministic.namedRoute;
        parsed.requiredPlaces = filterSpecificPlaces(Array.from(new Map(
          [...(parsed.requiredPlaces ?? []), ...deterministic.requiredPlaces]
            .map((place) => place.trim())
            .filter(Boolean)
            .map((place) => [place.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""), place]),
        ).values()));
        if (deterministic.startPlace) parsed.startPlace = deterministic.startPlace;
        if (typeof deterministic.startFromUserLocation === "boolean") {
          parsed.startFromUserLocation = deterministic.startFromUserLocation;
        }
        if (deterministic.geographicScope) parsed.geographicScope = deterministic.geographicScope;
        parsed.complexity = classifyTripComplexity({
          durationHours: parsed.durationHours,
          geographicScope: parsed.geographicScope,
          namedRoute: parsed.namedRoute,
          requiredPlaces: parsed.requiredPlaces,
          mode: parsed.mode,
          notes: [data.prompt, parsed.notes].filter(Boolean).join(" "),
        });
        enrichNotesWithHardConstraints(parsed, data.language);
      }
      return { parsed, error: null, tier: quota.tier, remaining: quota.remaining };
    } catch (e) {
      console.error("parseTripPrompt failed", e);
      await releaseFailedParse();
      if (fallback) return { parsed: fallback, error: null, tier: quota.tier, remaining: quota.remaining };
      return { parsed: null, error: "exception" };
    }
  });
